/**
 * Scroll-snap slideshow with dash indicators and optional autoplay.
 * Each slide sets its own display duration via data-duration (seconds).
 * Autoplay pauses on hover, focus, touch, and prefers-reduced-motion.
 * Inside the theme editor, selecting a slide block scrolls it into view.
 */
window.WsSlideshow = class WsSlideshow extends HTMLElement {
  #abort = null;
  #timer = null;
  #index = 0;
  #userPaused = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.track = this.querySelector('[data-slides]');
    this.slides = [...(this.track?.children ?? [])];
    this.dashes = [...this.querySelectorAll('[data-slide-to]')];

    if (!this.track || this.slides.length < 2) return;

    this.#syncActive();

    for (const dash of this.dashes) {
      dash.addEventListener('click', () => {
        this.#goTo(Number(dash.dataset.slideTo));
        this.#restartAutoplay(Number(dash.dataset.slideTo));
      }, { signal });
    }

    this.pauseBtn = this.querySelector('[data-slide-pause]');
    this.pauseBtn?.addEventListener('click', () => this.#togglePause(), { signal });

    this.track.addEventListener('scroll', () => this.#onScroll(), { signal, passive: true });

    if (this.hasAttribute('data-autoplay')) {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!reducedMotion.matches && !window.Shopify?.designMode) {
        this.#startAutoplay();
        this.addEventListener('pointerenter', () => this.#stopAutoplay(), { signal });
        this.addEventListener('pointerleave', () => this.#startAutoplay(), { signal });
        this.addEventListener('focusin', () => this.#stopAutoplay(), { signal });
        this.addEventListener('focusout', () => this.#startAutoplay(), { signal });
        document.addEventListener('visibilitychange', () => {
          document.hidden ? this.#stopAutoplay() : this.#startAutoplay();
        }, { signal });
      }
    }

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest?.('[data-slide-index]') ?? this.slides.find((s) => s.contains(event.target));
        if (slide && this.contains(slide)) this.#goTo(Number(slide.dataset.slideIndex));
      }, { signal });
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#stopAutoplay();
  }

  #goTo(index) {
    const target = this.slides[(index + this.slides.length) % this.slides.length];
    if (!target) return;
    this.track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
  }

  #onScroll() {
    const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
    if (index === this.#index) return;
    this.#index = index;
    this.#syncActive();
  }

  // Mark the current slide/dot and take off-screen slides out of the AT tree and
  // tab order (inert), so their CTAs aren't reachable while hidden.
  #syncActive() {
    this.dashes.forEach((dash, i) => {
      if (i === this.#index) dash.setAttribute('aria-current', 'true');
      else dash.removeAttribute('aria-current');
    });
    this.slides.forEach((slide, i) => {
      const hidden = i !== this.#index;
      slide.toggleAttribute('inert', hidden);
      slide.setAttribute('aria-hidden', String(hidden));
    });
  }

  #durationOf(index) {
    const seconds = Number(this.slides[index]?.dataset.duration);
    return Math.max(2, seconds || 6) * 1000;
  }

  #startAutoplay(fromIndex = this.#index) {
    if (this.#timer || this.#userPaused || !this.hasAttribute('data-autoplay')) return;
    this.#queueNext(fromIndex);
  }

  #togglePause() {
    this.#userPaused = !this.#userPaused;
    if (this.#userPaused) this.#stopAutoplay();
    else this.#startAutoplay();
    if (this.pauseBtn) {
      this.pauseBtn.toggleAttribute('data-paused', this.#userPaused);
      const label = this.#userPaused ? this.pauseBtn.dataset.labelPlay : this.pauseBtn.dataset.labelPause;
      if (label) this.pauseBtn.setAttribute('aria-label', label);
    }
  }

  #queueNext(fromIndex) {
    this.#timer = setTimeout(() => {
      const next = (fromIndex + 1) % this.slides.length;
      this.#goTo(next);
      this.#timer = null;
      this.#queueNext(next);
    }, this.#durationOf(fromIndex));
  }

  #stopAutoplay() {
    clearTimeout(this.#timer);
    this.#timer = null;
  }

  #restartAutoplay(fromIndex) {
    if (!this.hasAttribute('data-autoplay')) return;
    this.#stopAutoplay();
    this.#startAutoplay(fromIndex);
  }
}

if (!customElements.get('ws-slideshow')) customElements.define('ws-slideshow', WsSlideshow);
